<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import TrustedChoice from '$lib/PageSections/TrustedChoice/TrustedChoice.svelte';

	import peopleStanding from './images/AdobeStock_537421154.jpg';
	import bookshelfWoman from './images/bookshelf_woman_installation.jpg';
</script>

<Section>
	<p>
		<strong>Thinking of selling your home?</strong> You're not alone. Millions of
		Americans put their houses on the market every year. But in a competitive market,
		how do you make yours stand out and command a higher asking price?
	</p>
	<p>
		<strong>The answer</strong> lies in smart upgrades that enhance comfort, energy
		efficiency, and curb appeal. Indow window inserts tick all these boxes, offering
		a powerful and affordable way to increase the value of your home.
	</p>
	<h2>
		<span class="text-indow-grey-dark">The Problem:</span><br />Balancing
		Affordability and Resale Value
	</h2>
	<p>
		Upgrading windows is often cited as a top way to increase resale value.
		However, replacing existing windows can be a costly and time-consuming
		project. According to <a
			href="https://www.remodeling.hw.net/cost-vs-value/2023/"
			rel="external"
			target="_blank">Remodeling Magazine's Cost vs. Value Report</a
		>, a full window replacement recoups, on average, only around 72% of its
		cost at resale.
	</p>

	<Bubble class="mt-4! mb-12!">
		<span
			class="
     -mb-2 -ml-6 w-full text-left text-3xl font-normal text-indow-grey
     not-italic
   ">❝</span
		>
		<blockquote class="mb-0 text-xl font-bold italic">
			Replacing windows sits near the top of the list for cost-effective
			renovations, but it's important to factor in the total project cost, not
			just the price per window.
		</blockquote>
		<span
			class="
     -mt-2 -mr-6 w-full text-right text-3xl font-normal text-indow-grey
     not-italic
   ">❞</span
		>

		<p class="mb-0 w-full text-right font-extrabold text-indow-blue!">
			<span class="text-indow-grey-dark">–</span> Danielle Hale
		</p>
		<p class="w-full text-right italic">
			realtor.com Senior Home Improvement Editor
		</p>
	</Bubble>
	<Image src={peopleStanding} />
</Section>
<Section>
	<h2>
		<span class="text-indow-grey-dark">The Solution:</span><br />Invest in Indow
		Window Inserts
	</h2>
	<p>
		Indow window inserts offer a cost-effective way to significantly improve
		your home's energy efficiency, comfort, and noise reduction – all factors
		highly desired by potential buyers.
	</p>
	<p>
		Indow inserts install directly inside your existing window frames, creating
		a layer of superior insulation. This approach offers several advantages:
	</p>

	<ul
		class="
    list-none grid-flow-row
    max-md:grid
  "
	>
		<li
			class="
     mx-auto mb-8
     max-md:row-start-2
     md:float-right md:mb-4 md:ml-8 md:max-w-[33%]
   "
		>
			<Bubble>
				<span
					class="
       -mb-2 -ml-6 w-full py-0 text-left text-3xl font-normal text-indow-grey
       not-italic
     ">❝</span
				>
				<blockquote class="text-xl font-bold italic">
					Secondary glazing offers significant potential energy savings in
					existing buildings, particularly older buildings with leaky
					single-pane windows.
				</blockquote>
				<span
					class="
       -mt-2 -mr-6 w-full text-right text-3xl font-normal text-indow-grey
       not-italic
     ">❞</span
				>

				<p class="mb-0 w-full text-right font-extrabold text-indow-blue!">
					<span class="text-indow-grey-dark">–</span> Dr. Eleni Mistretta
				</p>
				<p class="w-full text-right italic">
					Staff Scientist, Lawrence Berkeley National Laboratory
				</p>
			</Bubble>
		</li>
		<li>
			<h3 class="text-left text-indow-grey-dark normal-case">
				Improved Energy Efficiency
			</h3>
			<p>
				The U.S. Department of Energy (DOE) recognizes secondary glazing (like
				Indow inserts) as one of the top 3 energy-saving home improvements.
				According to the <a
					href="https://www.energy.gov/eere/buildings/articles/role-highly-insulating-windows-achieving-50-energy-savings-residential"
					rel="external"
					target="_blank">DOE</a
				>, "[Secondary glazing] can reduce heat loss in winter by 50% or more,
				and heat gain in summer by 30% or more," potentially leading to lower
				energy bills. A study published by
				<a
					href="https://www.sciencedirect.com/journal/energy-and-buildings"
					rel="external"
					target="_blank">Energy and Buildings</a
				> found that "secondary glazing can significantly reduce space heating demand,"
				further highlighting its efficiency benefits.
			</p>
		</li>

		<li>
			<h3 class="text-left text-indow-grey-dark normal-case">
				Enhanced Comfort
			</h3>
			<p>
				Indow inserts create a quieter and more comfortable living environment.
				A <a
					href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4378508/"
					rel="external"
					target="_blank">study</a
				>
				by the National Institutes of Health (NIH) found that "excessive noise exposure
				can have a negative impact on human health and well-being." while a study
				by
				<a
					href="https://www.hsph.harvard.edu/news/features/noise-can-harm-your-health-even-if-you-sleep-through-it/"
					rel="external"
					target="_blank">Harvard University</a
				> found that chronic noise exposure can have negative health effects. Indow
				inserts significantly reduce noise pollution, creating a calmer, more peaceful
				home for you and future buyers.
			</p>
		</li>
		<li>
			<h3 class="text-left text-indow-grey-dark normal-case">
				Preserved Curb Appeal
			</h3>
			<p>
				Unlike bulky exterior storm windows, Indow inserts are virtually
				invisible from the outside and they require no modifications to your
				existing windows or window frames. This ensures your home maintains its
				architectural integrity and aesthetic appeal - a crucial factor for
				attracting buyers.
			</p>
		</li>
	</ul>

	<Bubble class="mb-14">
		<span
			class="
     -mb-2 -ml-6 w-full text-left text-3xl font-normal text-indow-grey
     not-italic
   ">❝</span
		>
		<blockquote class="mb-0 text-xl font-bold italic">
			Secondary glazing is a higly effective method for improving sound
			insulation in existing buildings.
		</blockquote>
		<span
			class="
     -mt-2 -mr-6 w-full text-right text-3xl font-normal text-indow-grey
     not-italic
   ">❞</span
		>

		<p class="mb-0 w-full text-right font-extrabold text-indow-blue!">
			<span class="text-indow-grey-dark">–</span> Dr. Jian Kang
		</p>
		<p class="w-full text-right italic">
			Professor of Building Science, University of Salford
		</p>
	</Bubble>
</Section>
<TrustedChoice>
	{#snippet subtitle()}
		<p
			class="
     text-indow-grey-dark
     max-md:text-left
   "
		>
			Here's why Indow is the best solution for maximizing your home's resale
			value:
		</p>
	{/snippet}
</TrustedChoice>
<Section>
	<h2>
		<span class="text-indow-grey-dark">Indow Window Inserts:</span><br />A Smart
		Investment for Your Future
	</h2>
	<p>
		By investing in Indow window inserts, you'll not only enjoy a more
		comfortable and energy-efficient home today, but you'll also be making a
		smart investment that can significantly increase your home's resale value
		when you’re ready to sell.
	</p>
	<p>
		<strong> Ready to learn more?</strong> Contact us today for a free consultation
		and discover how Indow window inserts can help you unlock the full potential of
		your home.
	</p>
	<Button href="/how-to/get-indow-inserts" class="mb-16"
		>Get A Free Estimate</Button
	>
	<Image src={bookshelfWoman} />
</Section>

<GetAFreeEstimate />
